@if ((organizationSelectors.loaded | select) && (selectors.loaded | select) && (availabilitySelectors.loaded | select)) {
  @let catalogRecord = selectors.catalogRecord | select;
  @let recordAvailability = availabilitySelectors.recordAvailability | select;
  @let description = selectors.description | select;
  @let isMdOrAbove = viewportSelectors.isMdAndUp | select;
  @let author = selectors.author | select:'';
  @let series = selectors.marcDetailsSeriesString | select:'';

  <div class="flex flex-col md:flex-row bg-bib-details-gradient px-4 py-4 gap-5 md:py-7 md:px-7 lg:px-10 md:gap-10 lg:gap-20 items-center md:items-start">
    <mx-cover-image [catalogRecord]="catalogRecord" [widthRem]="12.5" [heightRem]="isMdOrAbove ? 18 : 12.5" [priority]="true"/>
    <div class="flex flex-col gap-4 grow lg:pt-7 lg:pb-5">

      <!-- Title + Favorite button -->
      <div class="flex flex-col justify-between gap-y-1 lg:flex-row lg:gap-y-4">
        <!-- Title -->
        <div class="text-headline-large" mxTruncate [mxTruncateMaxLines]="3">
          {{ selectors.title | select | format: 'REPLACE_EMPTY' }}
        </div>

        <!-- Favorite / Unfavorite Button -->
        <div class="flex flex-row text-body-medium lg:min-w-[8.5rem] items-center">
          @if ((patronSelectors.hasPatron | select) && (favoriteSelectors.loaded | select) && catalogRecord) {
            <div class="flex flex-row items-center">
              @if (favoriteSelectors.isFavorite(catalogRecord.id) | select) {
                <mx-icon-button icon="favorite" color="error" (click)="removeFromFavorites(catalogRecord)"/>
                <p>Favorite</p>
              } @else {
                <mx-icon-button icon="favorite_border" iconClasses="text-on-surface-variant" (click)="addToFavorites(catalogRecord)"/>
                <p>Add to favorites</p>
              }
            </div>
          }
        </div>
      </div>

      <!-- Author, Publishing info -->
      <div class="flex flex-col gap-y-1">
        @if (author) {
          <mx-link-button [label]="author" linkType="inline" routeName="CATALOG_SEARCH" [queryParams]="{q: author, searchTarget: 'AUTHOR'}"
                          labelClasses="text-headline-small"/>
        } @else {
          <p class="text-headline-small">{{ DEFAULT_EMPTY_VALUE_REPLACEMENT_STRING }}</p>
        }
        <p class="text-title-large">{{ selectors.materialTypeDescription | select | format: 'REPLACE_EMPTY' }}</p>
        <p class="text-title-large">Published: {{ selectors.publicationDate | select | format: 'REPLACE_EMPTY' }}
          | {{ selectors.marcDetailsLanguageCode | select | format: 'REPLACE_EMPTY' }}
        </p>
        @if (series) {
          <mx-link-button [label]="series" linkType="inline" routeName="CATALOG_SEARCH" [queryParams]="{q: series, searchTarget: 'SERIES'}"
                          labelClasses="text-title-large"/>
        } @else {
          <p class="text-title-large">{{ DEFAULT_EMPTY_VALUE_REPLACEMENT_STRING }}</p>
        }
      </div>

      <!-- Description -->
      @if (isPopulatedString(description)) {
        <div class="flex flex-col items-start">
          @if (expandedDescription) {
            <div class="text-body-large">
              {{ selectors.description | select | format: 'REPLACE_EMPTY' }}
            </div>
          } @else {
            <div #description mxTruncate [mxTruncateMaxLines]="2" class="text-body-large" [mxTruncateShowTooltip]="false">
              {{ selectors.description | select | format: 'REPLACE_EMPTY' }}
            </div>
          }

          @if (showMoreLessButton) {
            <px-see-more-less-button [expanded]="expandedDescription" (click)="toggleExpandedDescription()"/>
          }
        </div>
      }
    </div>
  </div>

  <!-- Lower Section -->
  <div class="flex flex-col gap-5 py-4 md:py-7 px-4 md:px-7 lg:px-20">
    <!-- Availability + Place Hold -->
    <div class="flex flex-col md:flex-row gap-4 items-center md:items-start md:place-content-between">
      @if (catalogRecord && recordAvailability) {
        <px-catalog-home-branch-availability [availability]="recordAvailability"/>
        <px-catalog-hold-actions [catalogRecord]="catalogRecord"/>
      }
    </div>

    <!-- Availability Table -->
    <mat-card>
      <mat-card-content class="py-0 -mt-4 [&:has(.mx-expandable-row-row-based-companion-expanded:last-of-type)]:pb-2">
        <mx-table [tableDefinition]="tableDefinition"
                  class="[--mx-table-expanded-row-background-color:rgb(var(--mx-color-surface1))]"/>
      </mat-card-content>
    </mat-card>

    <!-- NoveList Section -->
    <px-nove-list [identifier]="selectors.identifier | select:''"
                  [noveListProfile]="organizationSelectors.noveListProfile | select:''"
                  [noveListPassword]="organizationSelectors.noveListPassword | select:''"/>

    <!-- Item Details Section -->
    <div class="flex flex-col px-2 md:px-8">
      <div class="text-headline-small mb-6">Item Details</div>
      <div class="grid grid-cols-[auto_1fr] md:grid-cols-[10rem_1fr] text-body-large gap-5">
        <div>Publisher, Date:</div>
        <div class="text-title-medium">{{ selectors.marcDetailsPublisher | select | format: 'REPLACE_EMPTY' }}
          , {{ selectors.marcDetailsPublicationDate | select | format: 'REPLACE_EMPTY' }}
        </div>
        <div>Author:</div>
        @if (author) {
          <mx-link-button [label]="author" linkType="inline" routeName="CATALOG_SEARCH" [queryParams]="{q: author, searchTarget: 'AUTHOR'}"
                          labelClasses="text-title-medium"/>
        } @else {
          <div class="text-title-medium">{{ DEFAULT_EMPTY_VALUE_REPLACEMENT_STRING }}</div>
        }
        <div>Description:</div>
        <div class="text-title-medium">{{ selectors.marcDetailsPhysicalDescription | select | format: 'REPLACE_EMPTY' }}</div>
        <div>Series:</div>
        @if (series) {
          <mx-link-button [label]="series" linkType="inline" routeName="CATALOG_SEARCH" [queryParams]="{q: series, searchTarget: 'SERIES'}"
                          labelClasses="text-title-medium"/>
        } @else {
          <div class="text-title-medium">{{ DEFAULT_EMPTY_VALUE_REPLACEMENT_STRING }}</div>
        }
        <div>Subjects:</div>
        <div class="text-title-medium">
          @for (subject of selectors.marcDetailsSubjects | select: []; track $index) {
            <div>{{ subject }}</div>
          }
        </div>
        <div>Genre:</div>
        <div class="text-title-medium">
          @for (genre of selectors.marcDetailsGenres | select: []; track $index) {
            <div>{{ genre }}</div>
          }
        </div>
        <div>ISBN:</div>
        <div class="text-title-medium">
          @for (isbn of selectors.displayableIdentifiers | select: []; track $index) {
            <div>{{ isbn }}</div>
          }
        </div>
      </div>
    </div>

    <!-- Suggested Items Carousel -->
    <px-catalog-carousel-card carouselTitle="Other items you might like"
                              [carouselItems]="relatedItemsSelectors.relatedItems | select:[]"
                              noItemsMessage="There are no items available to show."
                              titleClasses="text-headline-small pb-0"/>
  </div>
} @else {
  <div class="h-[22rem] w-full bg-bib-details-gradient"></div>
  <div class="h-[22rem] w-full"></div>
}
